import i18n, { currentLanguage } from '@/i18n';
import { enqueue, flushOutbox, type QueuedRequest } from '@/lib/offline';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';
const ACCESS_TOKEN_KEY = 'dgi.accessToken';
const REFRESH_TOKEN_KEY = 'dgi.refreshToken';

export interface ApiErrorPayload {
  code: string;
  message: string;
  messages?: { fr: string; en: string };
  details?: unknown;
}

/** Erreur normalisee remontee par l'API, avec ses traductions. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly translations?: { fr: string; en: string };

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = payload.code;
    this.details = payload.details;
    this.translations = payload.messages;
  }

  /** Message dans la langue courante de l'interface. */
  localized(): string {
    return this.translations?.[currentLanguage()] ?? this.message;
  }
}

export const tokenStore = {
  get access(): string | null {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  get refresh(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(accessToken: string, refreshToken?: string): void {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } catch {
      /* stockage indisponible (navigation privee) */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      /* stockage indisponible */
    }
  },
};

/** Notifie l'application qu'une session a expire (redirection vers la connexion). */
export const SESSION_EXPIRED_EVENT = 'dgi:session-expired';

function emitSessionExpired(): void {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Desactive la tentative de rafraichissement automatique du jeton. */
  skipRefresh?: boolean;
  raw?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `${url}?${serialized}` : url;
}

let refreshPromise: Promise<boolean> | null = null;

/**
 * Rafraichit le jeton d'acces. Les appels concurrents partagent la meme
 * promesse afin de n'emettre qu'une seule requete de rotation.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = tokenStore.refresh;
    if (!refreshToken) return false;

    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = (await response.json()) as { accessToken: string; refreshToken: string };
      tokenStore.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const payload = (await response.json()) as { error?: ApiErrorPayload };
    if (payload.error) return new ApiError(response.status, payload.error);
  } catch {
    /* corps non JSON */
  }
  return new ApiError(response.status, {
    code: 'INTERNAL_ERROR',
    message: i18n.t('errors.generic'),
  });
}

/**
 * Chemins dont l'echec reseau est mis en file d'attente : ils enregistrent une
 * progression, jamais une lecture. Rejouer une lecture n'aurait aucun sens.
 * Paths whose network failure is queued: they record progress, never a read.
 */
const QUEUEABLE = [/^\/progress\/sessions\//];

async function execute(path: string, options: RequestOptions = {}): Promise<Response> {
  const { body, query, skipRefresh, headers, ...rest } = options;

  const requestHeaders = new Headers(headers);
  requestHeaders.set('X-Lang', currentLanguage());
  if (body !== undefined && !(body instanceof FormData)) {
    requestHeaders.set('Content-Type', 'application/json');
  }
  const token = tokenStore.access;
  if (token) requestHeaders.set('Authorization', `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      ...rest,
      headers: requestHeaders,
      credentials: 'include',
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    });
  } catch (cause) {
    // Hors ligne : la progression est mise de cote et rejouee au retour du
    // reseau ; l'agent poursuit sa session sans interruption.
    const method = (rest.method ?? 'GET').toUpperCase();
    if (QUEUEABLE.some((pattern) => pattern.test(path)) && method !== 'GET' && !(body instanceof FormData)) {
      await enqueue({ path, method: method as 'POST' | 'PUT' | 'PATCH', body });
      return new Response(null, { status: 202, statusText: 'Queued offline' });
    }
    throw cause;
  }

  // Jeton expire : on tente une rotation unique puis on rejoue la requete.
  if (response.status === 401 && !skipRefresh && tokenStore.refresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return execute(path, { ...options, skipRefresh: true });
    }
    tokenStore.clear();
    emitSessionExpired();
  }

  return response;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await execute(path, options);

  if (!response.ok) throw await parseError(response);
  // 204 sans contenu, 202 mis en file d'attente hors ligne.
  if (response.status === 204 || response.status === 202) return undefined as T;

  return (await response.json()) as T;
}

/** Recupere une reponse binaire (PDF, CSV) sous forme de blob. */
async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const response = await execute(path, options);
  if (!response.ok) throw await parseError(response);
  return response.blob();
}

/**
 * Rejoue la file d'attente hors ligne avec le jeton courant.
 * Replays the offline outbox with the current token.
 */
export function replayOutbox(): Promise<number> {
  return flushOutbox((entry: QueuedRequest) => {
    const headers = new Headers({ 'Content-Type': 'application/json', 'X-Lang': currentLanguage() });
    const token = tokenStore.access;
    if (token) headers.set('Authorization', `Bearer ${token}`);

    return fetch(buildUrl(entry.path), {
      method: entry.method,
      headers,
      credentials: 'include',
      body: JSON.stringify(entry.body),
    });
  });
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
  blob: requestBlob,
};
